import type { FC } from 'react';
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import { useLocation } from 'react-router';
import { LoadFromServer } from '../../LoadFromServer';
import { Redirect } from '../../components/redirect/Redirect';
import { isServer } from '../../utils/utils';
import { BloomreachProductType } from '../search/hooks/use-search';
import { useSearch } from '../search/hooks/use-search/useSearch';
import { cleanPathname } from '../search/utils/search-url-params-utils';
import { configHtmlCache } from '../utils/config-html-cache';
import { ShowCategoryPage } from './ShowCategoryPage';
import { isCategoryInfo } from './api/getContentData';
import { useContentData } from './hooks/useContentData';

/**
 * This component is used to dynamically route to the correct page based on the path.
 * It is used to handle the case where the category is not found on SFCC and redirect to the category path on SFCC.
 * It is also used to handle the case where the category is found on SFCC and redirect to the category page.
 */
const DynamicCategory: FC = () => {
    const serverContext = useServerContext();
    const location = useLocation();
    const pathname = cleanPathname(location.pathname);
    // Trick to make the search query not dependant on the contentInfo
    useSearch(BloomreachProductType.hardgood, pathname);
    const { data: contentInfo } = useContentData({
        categoryPath: pathname,
    });
    if (isServer && serverContext) {
        configHtmlCache(serverContext.res);
    }
    if (
        contentInfo?.response?.type === 'REDIRECT' &&
        contentInfo?.response?.urlMapping?.destinationUrl
    ) {
        return (
            <Redirect
                to={contentInfo?.response?.urlMapping?.destinationUrl}
                type="sg"
                status={contentInfo?.response?.urlMapping?.statusCode}
            />
        );
    }

    if (isCategoryInfo(contentInfo)) {
        return <ShowCategoryPage categoryInfo={contentInfo} />;
    }

    // If the category is not found, redirect to the category path on SFCC
    return <LoadFromServer />;
};

export default DynamicCategory;
